// src/pages/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import {
  Layout,
  Menu,
  Typography,
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Avatar,
  Dropdown,
  Space,
  Badge,
  Alert
} from 'antd';
import {
  DashboardOutlined,
  SettingOutlined,
  TeamOutlined,
  ShoppingOutlined,
  LogoutOutlined,
  UserOutlined,
  BellOutlined,
  EyeOutlined,
  EditOutlined,
  PlusOutlined,
  CalendarOutlined,
  CreditCardOutlined,
  BulbOutlined,
} from '@ant-design/icons';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../components/ParticleBackground';
import ParticleBackground from '../components/ParticleBackground';
import ServicesManagement from '../components/admin/ServicesManagement';
import BookingsManagement from '../components/admin/BookingsManagement';
import UsersManagement from '../components/admin/UsersManagement';
import PaymentsManagement from '../components/admin/PaymentsManagement';
import AdminCalendarView from '../components/admin/AdminCalendarView';
import './AdminDashboard.css';
import ekahLogo from '../assets/ekah-single.jpg';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;

const AdminDashboard = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState('dashboard');
  const { currentAdmin, adminLogout, hasPermission } = useAdminAuth();
  const { toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentAdmin) {
      navigate('/admin/login');
    }
  }, [currentAdmin, navigate]);

  const handleLogout = async () => {
    try {
      await adminLogout();
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const userMenu = (
    <Menu
      theme={isDark ? 'dark' : 'light'}
      items={[
        {
          key: 'profile',
          icon: <UserOutlined />,
          label: 'Profile',
        },
        {
          key: 'settings',
          icon: <SettingOutlined />,
          label: 'Settings',
        },
        {
          type: 'divider',
        },
        {
          key: 'logout',
          icon: <LogoutOutlined />,
          label: 'Logout',
          onClick: handleLogout,
        },
      ]}
    />
  );

  const menuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: 'calendar',
      icon: <CalendarOutlined />,
      label: 'Calendar',
      disabled: !hasPermission('view_bookings'),
    },
    {
      key: 'bookings',
      icon: <BellOutlined />,
      label: 'Bookings',
      disabled: !hasPermission('view_bookings'),
    },
    {
      key: 'services',
      icon: <ShoppingOutlined />,
      label: 'Services',
    },
    {
      key: 'payments',
      icon: <CreditCardOutlined />,
      label: 'Payments',
      disabled: !hasPermission('manage_payments'),
    },
    {
      key: 'users',
      icon: <TeamOutlined />,
      label: 'Users',
      disabled: !hasPermission('view_users'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      disabled: !hasPermission('manage_settings'),
    },
  ];

  const renderContent = () => {
    switch (selectedMenu) {
      case 'calendar':
        return <AdminCalendarView isDark={isDark} />;
      case 'bookings':
        return <BookingsManagement />;
      case 'services':
        return <ServicesManagement />;
      case 'payments':
        return <PaymentsManagement />;
      case 'users':
        return <UsersManagement />;
      case 'settings':
        return (
          <Card className="dashboard-info-card">
            <Title level={3}>System Settings</Title>
            <Text type="secondary">Basic system settings coming soon...</Text>
          </Card>
        );
      default:
        return (
          <div>
            <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
              <Col span={24}>
                <Alert
                  message="Admin Dashboard"
                  description="Welcome to the Ekah Health administration panel. Manage services, users, and system settings from here."
                  type="info"
                  showIcon
                  style={{ borderRadius: '8px' }}
                />
              </Col>
            </Row>

            <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
              <Col xs={24} sm={12} lg={6}>
                <Card className="dashboard-stat-card">
                  <Statistic
                    title="Total Services"
                    value={10}
                    prefix={<ShoppingOutlined style={{ color: '#1890ff' }} />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card className="dashboard-stat-card">
                  <Statistic
                    title="Active Users"
                    value={128}
                    prefix={<TeamOutlined style={{ color: '#52c41a' }} />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card className="dashboard-stat-card">
                  <Statistic
                    title="Total Bookings"
                    value={342}
                    prefix={<EyeOutlined style={{ color: '#faad14' }} />}
                    valueStyle={{ color: '#faad14' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card className="dashboard-stat-card">
                  <Statistic
                    title="Revenue (₹)"
                    value={25680}
                    prefix={<span style={{ color: '#f5222d' }}>₹</span>}
                    valueStyle={{ color: '#f5222d' }}
                  />
                </Card>
              </Col>
            </Row>

            <Row gutter={[24, 24]}>
              <Col xs={24} lg={16}>
                <Card
                  title="Quick Actions"
                  className="dashboard-info-card"
                  extra={<Button type="link">View All</Button>}
                >
                  <Space wrap>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => setSelectedMenu('services')}
                    >
                      Add Service
                    </Button>
                    <Button
                      icon={<EditOutlined />}
                      onClick={() => setSelectedMenu('services')}
                      style={{
                        background: isDark ? '#333' : '#fff',
                        borderColor: isDark ? '#444' : '#d9d9d9',
                        color: isDark ? '#fff' : 'inherit'
                      }}
                    >
                      Manage Services
                    </Button>
                    <Button
                      icon={<TeamOutlined />}
                      onClick={() => setSelectedMenu('users')}
                      style={{
                        background: isDark ? '#333' : '#fff',
                        borderColor: isDark ? '#444' : '#d9d9d9',
                        color: isDark ? '#fff' : 'inherit'
                      }}
                    >
                      View Users
                    </Button>
                  </Space>
                </Card>
              </Col>
              <Col xs={24} lg={8}>
                <Card
                  title="System Status"
                  className="dashboard-info-card"
                >
                  <div style={{ textAlign: 'center' }}>
                    <Badge status="success" text={<span style={{ color: isDark ? '#fff' : 'inherit' }}>All systems operational</span>} />
                    <div style={{ marginTop: '16px' }}>
                      <Text type="secondary">Last updated: Just now</Text>
                    </div>
                  </div>
                </Card>
              </Col>
            </Row>
          </div>
        );
    }
  };


  if (!currentAdmin) {
    return null;
  }

  // Common Header style object used in both themes (overridden by CSS classes but good for inline defaults)
  const headerStyle = {
    padding: '0 24px',
    background: isDark ? '#141414' : '#fff', // Match theme
    borderBottom: isDark ? '1px solid #303030' : '1px solid #f0f0f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    zIndex: 10
  };

  return (
    <ParticleBackground>
      <Layout className="admin-dashboard-container" style={{ minHeight: '100vh' }}>
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          width={260}
          className={`admin-dashboard-sider ${isDark ? 'dark-sider' : 'light-sider'}`}
          style={{
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 100,
            boxShadow: '4px 0 24px rgba(0,0,0,0.05)',
            borderRight: isDark ? '1px solid #303030' : '1px solid #f0f0f0'
          }}
          theme={isDark ? 'dark' : 'light'}
        >
          <div className="admin-logo-container" style={{
            margin: '16px',
            height: '64px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            paddingLeft: collapsed ? '0' : '8px',
            gap: '12px'
          }}>
            <div style={{
              width: collapsed ? '40px' : '48px',
              height: collapsed ? '40px' : '48px',
              background: '#fff',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              flexShrink: 0,
              padding: '4px',
              overflow: 'hidden'
            }}>
              <img
                src={ekahLogo}
                alt="Ekah Health"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }}
              />
            </div>

            {!collapsed && (
              <span style={{
                color: isDark ? '#fff' : '#1f2937',
                fontWeight: 'bold',
                fontSize: '22px',
                lineHeight: '1.2',
                fontFamily: "'Outfit', sans-serif"
              }}>
                EkahHealth
              </span>
            )}
          </div>

          <Menu
            theme={isDark ? 'dark' : 'light'}
            mode="inline"
            selectedKeys={[selectedMenu]}
            items={menuItems}
            onClick={({ key }) => setSelectedMenu(key)}
            style={{ border: 'none', background: 'transparent' }}
          />
        </Sider>

        <Layout style={{ marginLeft: collapsed ? 80 : 260, transition: 'all 0.2s' }}>
          <Header className="admin-dashboard-header" style={headerStyle}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Title level={4} style={{ margin: 0, fontSize: '20px' }}>
                {menuItems.find(item => item.key === selectedMenu)?.label || 'Dashboard'}
              </Title>
            </div>

            <Space size={16}>
              <Button
                type="text"
                shape="circle"
                icon={isDark ? <BulbOutlined style={{ color: '#fff' }} /> : <BulbOutlined />}
                onClick={toggleTheme}
                style={{ width: '40px', height: '40px' }}
              />
              <Button
                type="text"
                shape="circle"
                icon={isDark ? <BellOutlined style={{ color: '#fff' }} /> : <BellOutlined />}
                style={{ width: '40px', height: '40px' }}
              />
              <Dropdown overlay={userMenu} placement="bottomRight" trigger={['click']}>
                <div style={{
                  cursor: 'pointer',
                  padding: '4px 12px',
                  borderRadius: '24px',
                  background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                  transition: 'all 0.3s'
                }}>
                  <Space>
                    <Avatar
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        verticalAlign: 'middle',
                        border: '2px solid rgba(255,255,255,0.2)'
                      }}
                      size="small"
                    >
                      {currentAdmin?.name?.charAt(0) || 'A'}
                    </Avatar>
                    <span style={{ fontWeight: 500, color: isDark ? '#fff' : '#333' }}>{currentAdmin?.name}</span>
                  </Space>
                </div>
              </Dropdown>
            </Space>
          </Header>

          <Content style={{
            margin: '24px',
            minHeight: 280,
            overflow: 'initial'
          }}>
            {/* Main Content */}
            <div style={{
              minHeight: '100%',
              borderRadius: '16px',
            }}>
              {renderContent()}
            </div>
          </Content>
        </Layout>
      </Layout>
    </ParticleBackground >
  );
};

export default AdminDashboard;